/**
 * LaneAttribution — Collapsible footer showing lane contributions
 *
 * "How this answer was built" expandable section with percentages per lane.
 */
import React, { useState } from 'react';
import type { LaneAttributionData } from '../types';
import { ChevronRight } from '../../shared';

interface LaneAttributionProps {
  data: LaneAttributionData;
}

export const LaneAttribution: React.FC<LaneAttributionProps> = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const { lanes, fastestLane, winnerLane } = data;

  if (!lanes || lanes.length === 0) return null;

  return (
    <div className="rounded-xl border border-cyan-400/10 overflow-hidden">
      {/* Toggle header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-cyan-400/5 transition-colors group"
      >
        <span className="text-xs text-cyan-400/50 font-medium group-hover:text-cyan-400/70 transition-colors">
          How this answer was built
        </span>
        <ChevronRight
          className={`w-3.5 h-3.5 text-cyan-400/30 group-hover:text-cyan-400/60 transition-all duration-200 ${
            expanded ? 'rotate-90' : ''
          }`}
        />
      </button>

      {/* Expandable content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Lane bars */}
          {lanes.map((lane) => (
            <div key={lane.id} className="flex items-center gap-3">
              <div className="flex items-center gap-2 w-28 shrink-0">
                <span className="text-sm">{lane.emoji}</span>
                <span className="text-xs text-cyan-400/70 font-medium">{lane.label}</span>
              </div>
              <div className="flex-1 h-2 rounded-full bg-cyan-400/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-cyan-400/50 transition-all duration-500"
                  style={{ width: `${lane.percentage}%` }}
                />
              </div>
              <span className="w-10 text-right text-xs text-cyan-400/50 font-medium">
                {lane.percentage}%
              </span>
            </div>
          ))}

          {/* Footer metadata */}
          <div className="flex flex-wrap gap-3 pt-2 border-t border-cyan-400/10 text-[10px] text-cyan-400/40">
            {fastestLane && (
              <span>
                ⏱️ Fastest: <strong className="text-cyan-400/60">{fastestLane}</strong>
              </span>
            )}
            {winnerLane && (
              <span>
                🏆 Winner: <strong className="text-cyan-400/60">{winnerLane}</strong>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LaneAttribution;
